import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_GH = "https://wa.me/233209003178";
const WHATSAPP_CI = "https://wa.me/2250769921299";

const inputCls =
  "w-full rounded-none border-b border-[#DCD6CE] bg-transparent px-0 py-3 text-[#141210] placeholder:text-[#8A837B] focus:border-neon-teal focus:outline-none focus:ring-0 transition-colors duration-300";

export default function Contact() {
  const { t, lang } = useLang();
  const empty = { first_name: "", last_name: "", email: "", phone: "", topic: "", message: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, lang });
      toast.success(t("contact.success"));
      setForm(empty);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-[#E6E2DC] bg-surface py-24 md:py-36"
    >
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-6 md:grid-cols-2 md:px-12">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-neon-teal"
            data-testid="contact-eyebrow"
          >
            {t("contact.eyebrow")}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-4xl font-medium tracking-tight text-[#141210] md:text-6xl"
            data-testid="contact-heading"
          >
            {t("contact.headingA")} <span className="text-neon-teal">{t("contact.headingB")}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 max-w-md text-base leading-relaxed text-[#6B655F] md:text-lg"
          >
            {t("contact.intro")}
          </motion.p>

          <div className="mt-12 flex flex-col gap-5" data-testid="contact-details">
            <a
              href="mailto:info@thegeekslabs.com"
              data-testid="contact-email-link"
              className="group flex items-center gap-4 text-[#141210] transition-colors duration-300 hover:text-neon-teal"
            >
              <Mail size={18} className="text-neon-teal" />
              info@thegeekslabs.com
            </a>
            <a
              href="tel:+17325244574"
              data-testid="contact-phone-link"
              className="group flex items-center gap-4 text-[#141210] transition-colors duration-300 hover:text-neon-teal"
            >
              <Phone size={18} className="text-neon-teal" />
              +1 732 524 4574
            </a>
            <div className="flex items-center gap-4 text-[#141210]" data-testid="contact-location">
              <MapPin size={18} className="text-neon-teal" />
              Tema, Ghana · Abidjan, Côte d'Ivoire
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <motion.a
              href={WHATSAPP_GH}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-ghana-button"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="inline-flex items-center gap-3 bg-neon-green px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] text-[#FAF8F5] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#141210]"
            >
              <MessageCircle size={18} />
              {t("contact.waGhana")}
            </motion.a>
            <motion.a
              href={WHATSAPP_CI}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-abidjan-button"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="inline-flex items-center gap-3 border border-neon-green/60 px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] text-neon-green transition-[background-color,color,transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-neon-green hover:bg-neon-green hover:text-[#FAF8F5]"
            >
              <MessageCircle size={18} />
              {t("contact.waAbidjan")}
            </motion.a>
          </div>
          <div className="mt-6 flex flex-col gap-2 text-sm text-[#6B655F]" data-testid="whatsapp-numbers">
            <span>{t("contact.ghanaLabel")}: +233 20 900 3178</span>
            <span>{t("contact.ciLabel")}: +225 07 69 92 12 99</span>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1] }}
          className="border border-[#E6E2DC] bg-white p-8 backdrop-blur-2xl md:p-12"
        >
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-[#6B655F]">
                {t("contact.firstName")}
              </label>
              <input
                data-testid="contact-first-name-input"
                required
                value={form.first_name}
                onChange={set("first_name")}
                placeholder={t("contact.firstPh")}
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-[#6B655F]">
                {t("contact.lastName")}
              </label>
              <input
                data-testid="contact-last-name-input"
                required
                value={form.last_name}
                onChange={set("last_name")}
                placeholder={t("contact.lastPh")}
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-[#6B655F]">
                {t("contact.email")}
              </label>
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder={t("contact.emailPh")}
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-[#6B655F]">
                {t("contact.phone")}
              </label>
              <input
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder={t("contact.phonePh")}
                className={inputCls}
              />
            </div>
          </div>
          <div className="mt-8">
            <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-[#6B655F]">
              {t("contact.topicLabel")}
            </label>
            <select
              data-testid="contact-topic-select"
              value={form.topic}
              onChange={set("topic")}
              className={`${inputCls} cursor-pointer bg-transparent [&>option]:bg-surface`}
            >
              <option value="">{t("contact.topicPlaceholder")}</option>
              {t("contact.topics").map((topic) => (
                <option key={topic}>{topic}</option>
              ))}
            </select>
          </div>
          <div className="mt-8">
            <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-[#6B655F]">
              {t("contact.message")}
            </label>
            <textarea
              data-testid="contact-message-input"
              required
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder={t("contact.msgPh")}
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={sending}
            className="mt-10 inline-flex items-center gap-3 bg-[#141210] px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] text-[#FAF8F5] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-neon-teal disabled:opacity-60"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sending ? t("contact.sending") : t("contact.submit")}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
